import * as bootstrap from 'bootstrap';
import * as countries from 'smg/countries';
import * as models from 'smg/customer-api/models';
import { DeleteUserTaxExemption, NewUserTaxExemption, RestoreUserTaxExemption, UserTaxExemptions } from 'smg/customer-api/usertaxexemptions';
import { ShowError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import * as html from 'smg/html';
import * as template from 'smg/template';
import * as toast from 'smg/toast';

import { Uploader } from '../components/uploader';
import * as usertaxexemptions from '../usertaxexemptions';

import { State } from './state';

// SetupTax wires the "Tax Exemption" accordion: yes/no toggle, the list of
// saved exemptions, new/edit form with uploads, and delete-with-undo.
export function SetupTax(state: State): void {
    state.initTax = () => initTax(state);
    state.updateTaxExemptionOptions = userID => updateTaxExemptionOptions(state, userID);
    state.updateTaxExemptionSummary = () => updateTaxExemptionSummary(state);
    state.promptTaxExemption = ute => promptTaxExemption(state, ute);
    state.checkTaxSelected = () => checkTaxSelected(state);

    bindTaxToggles(state);
    bindTaxConfirmNone(state);
    bindTaxCollapse(state);
    bindTaxRadioClick(state);
}

function checkTaxSelected(state: State): void {
    const { dom } = state;
    if (dom.$taxToggleYes.checked) {
        dom.$taxDetailsParent?.classList.remove('d-none');
        dom.$taxDetailsParent?.toggleAttribute('disabled', false);
        dom.$taxConfirmNone.parentElement?.classList.add('d-none');
    } else {
        dom.$taxDetailsParent?.classList.add('d-none');
        dom.$taxDetailsParent?.toggleAttribute('disabled', true);
        dom.$taxConfirmNone.parentElement?.classList.remove('d-none');
    }
}

async function initTax(state: State): Promise<void> {
    const { dom } = state;
    dom.$taxBodyToggle?.classList.add('d-none');
    checkTaxSelected(state);
    state.startLoading();

    if (state.sess?.user.id) {
        await updateTaxExemptionOptions(state, state.sess.user.id).then(async () => {
            dom.$taxDetailsParent?.replaceChildren();
            if (state.userTaxExemptions.length) {
                await drawTaxExemptionList(state);
            } else {
                promptTaxExemption(state);
            }
        });
    } else {
        promptTaxExemption(state, state.taxExemption);
    }
    state.stopLoading();
}

async function drawTaxExemptionList(state: State): Promise<void> {
    const { dom } = state;
    const drawPromises = state.userTaxExemptions.map(ute =>
        usertaxexemptions.Draw(ute, { radio: true }).
            then(drawEl => dom.$taxDetailsParent?.appendChild(drawEl)).
            catch(ShowError));

    await Promise.all(drawPromises).then(() => {
        dom.$taxDetailsParent?.appendChild(template.Clone('#tax-exemption-buttons'));
        dom.$taxDetailsParent?.querySelector<HTMLButtonElement>('.tax-exemption-new')?.
            addEventListener('click', () => promptTaxExemption(state));
        dom.$taxDetailsParent?.querySelector<HTMLButtonElement>('.tax-exemption-continue')?.
            addEventListener('click', () => handleContinue(state));

        bindTaxExemptionRowButtons(state);

        let $firstTaxExemption = dom.$taxDetailsParent?.querySelector<HTMLInputElement>('.address:first-child input[type="radio"]');
        if (state.taxExemption?.id) {
            $firstTaxExemption = dom.$taxDetailsParent?.querySelector<HTMLInputElement>(`input[value="${state.taxExemption.id}"]`);
        }
        if ($firstTaxExemption) {
            $firstTaxExemption.checked = true;
        }
    }).then(() => {
        if (state.taxFirstOpen) {
            if (state.taxExemption || state.userTaxExemptions.length > 0) {
                dom.$taxToggleYes.click();
            } else {
                dom.$taxToggleNo.click();
            }
        }
        state.taxFirstOpen = false;
    });
}

async function handleContinue(state: State): Promise<void> {
    const { dom } = state;
    const selected = dom.$taxDetailsParent?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
    if (selected) {
        const ute = state.userTaxExemptions.find(u => u.id === selected.value);
        if (ute) {
            state.taxExemption = ute;
            state.taxExemptionUploadThumbnails = [];
            state.customer = await state.getUser();
            state.taxStepComplete = true;
            void state.updateOrderSummary();
            state.updateShippingSummary();
            void state.updateTaxExemptionSummary();
        }
        updateTaxEditLink(state);
        dom.$paymentBodyToggle?.click();
    }
}

function bindTaxExemptionRowButtons(state: State): void {
    const { dom } = state;
    dom.$taxDetailsParent?.querySelectorAll<HTMLElement>('.user-tax-exemption').forEach($ute => {
        const id = $ute.querySelector<HTMLInputElement>('input[type="radio"]')?.value || '';
        if (id === '') {
            return;
        }

        const $edit = $ute.querySelector<HTMLButtonElement>('.user-tax-exemption-edit');
        $edit?.addEventListener('click', () => {
            const ute = state.userTaxExemptions.find(u => u.id === id);
            if (ute) {
                promptTaxExemption(state, ute);
            }
        });

        const $delete = $ute.querySelector<HTMLButtonElement>('.user-tax-exemption-delete');
        $delete?.addEventListener('click', () => promptDeleteTaxExemption(state, $ute, id));
    });
}

function promptDeleteTaxExemption(state: State, $ute: HTMLElement, id: string): void {
    const deleteTooltip = new bootstrap.Tooltip($ute, {
        trigger: 'manual',
        placement: 'top',
        title: 'Are you sure you want to delete this tax exemption?',
        html: true,
        offset: [0, 16],
    });

    const $deleteConfirm = template.Clone(document.querySelector('.delete-confirm-template') as HTMLTemplateElement).firstElementChild as HTMLElement;
    const $confirm = $deleteConfirm.querySelector('.delete-confirm-button') as HTMLButtonElement;
    $confirm.addEventListener('click', async () => {
        await DeleteUserTaxExemption(id).then(() => {
            toast.Undo('Deleted tax exemption.', async () => {
                await RestoreUserTaxExemption(id).then(() => initTax(state)).then(() => state.dom.$taxToggleYes.click());
            });
            void initTax(state);
            deleteTooltip.dispose();
        });
    });

    const $cancel = $deleteConfirm.querySelector('.delete-cancel-button') as HTMLButtonElement;
    $cancel.addEventListener('click', () => deleteTooltip.dispose());

    const $msg = $deleteConfirm.querySelector('.delete-confirm-message') as HTMLDivElement;
    $msg.textContent = 'Are you sure you want to delete this tax exemption?';
    deleteTooltip.setContent({ '.tooltip-inner': $deleteConfirm });
    deleteTooltip.show();
}

async function updateTaxExemptionOptions(state: State, userID: string): Promise<void> {
    if (userID === '') {
        return;
    }
    state.userTaxExemptions = [];

    const loadUserTaxExemptions = (q = '') => UserTaxExemptions({
        data: 'number,countryID,stateID,userTaxExemptionUploads,userTaxExemptionReasonID,userTaxExemptionReason',
        sort: 'lastOrdered desc,created desc',
        max: 1,
        q,
    }).then(data => data.userTaxExemptions);

    const fetch = async (id?: string) => {
        if (id) {
            const byID = await loadUserTaxExemptions(`id:${id}`);
            if (byID.length) {
                return byID;
            }
        }
        return loadUserTaxExemptions();
    };

    await fetch().then(resp => {
        state.userTaxExemptions = resp;
        state.userTaxExemptions.sort((a, b) =>
            (b.updated.isValid ? b.updated.toMillis() : 0) - (a.updated.isValid ? a.updated.toMillis() : 0));
    });
}

async function updateTaxExemptionSummary(state: State): Promise<void> {
    const { dom } = state;
    dom.$taxPreview.classList.value = 'preview';
    if (!state.taxExemption) {
        dom.$taxPreview?.replaceChildren(html.FromString('<div class="w-100 py-16 px-md-20 text-body-secondary text-center fs-4 fst-italic">No tax exemption selected</div>'));
        return;
    }

    dom.$taxPreview.classList.value = 'preview px-md-17 pt-2 pb-10 d-flex flex-column flex-md-row';
    dom.$taxPreview?.replaceChildren(template.Clone(dom.$taxPreviewTemplate));
    const $type = dom.$taxPreview?.querySelector<HTMLSpanElement>('.tax-preview-type') as HTMLSpanElement;
    const $number = dom.$taxPreview?.querySelector<HTMLSpanElement>('.tax-preview-number') as HTMLSpanElement;
    const $state = dom.$taxPreview?.querySelector<HTMLSpanElement>('.tax-preview-state') as HTMLSpanElement;

    $type.textContent = state.taxExemption.userTaxExemptionReason?.name || '';
    $number.textContent = state.taxExemption.number || '';

    const stateCode = await countries.GetState(state.taxExemption.countryID || 0, state.taxExemption.stateID || 0).then(s => s?.stateCode || 'US');
    $state.textContent = stateCode;

    const $attachmentsContainer = dom.$taxPreview?.querySelector<HTMLDivElement>('.attachments-container') as HTMLDivElement;
    state.taxExemptionUploadThumbnails.forEach($thumb => {
        const $clone = $thumb.cloneNode(true) as HTMLElement;
        $clone.querySelector('.uploader-thumbnail-delete')?.remove();
        $clone.querySelector('a[data-fancybox]')?.setAttribute(
            'data-fancybox',
            `tax-exemption-${state.taxExemption?.id ?? 'summary'}`,
        );
        $attachmentsContainer.appendChild($clone);
    });
}

function promptTaxExemption(state: State, uteInit?: models.IUserTaxExemption): void {
    const ute = uteInit ? new models.UserTaxExemption(uteInit) : undefined;
    const { dom } = state;
    if (!dom.$taxDetailsParent) {
        return;
    }

    dom.$taxDetailsParent.replaceChildren(template.Clone('#new-tax-exemption-form'));
    dom.$taxDetailsParent.classList.add('show');

    const $form = dom.$taxDetailsParent.querySelector<HTMLFormElement>('form') as HTMLFormElement;
    const $details = dom.$taxDetailsParent.querySelector<HTMLDivElement>('#tax-exemption-details') as HTMLDivElement;
    const $type = $details.querySelector<HTMLSelectElement>('#tax-exemption-type') as HTMLSelectElement;
    const $number = $details.querySelector<HTMLInputElement>('#tax-exemption-number') as HTMLInputElement;
    const $stateSelect = $details.querySelector<HTMLSelectElement>('#tax-exemption-state') as HTMLSelectElement;
    const $taxUploaderInput = $details.querySelector<HTMLInputElement>('#tax-exemption-uploader') as HTMLInputElement;
    const $taxUploaderLabel = $taxUploaderInput.closest('.uploader-dropzone') as HTMLLabelElement;
    const $taxConfirm = dom.$taxDetailsParent.querySelector<HTMLButtonElement>('#new-tax-exemption-confirm') as HTMLButtonElement;
    const $taxCancel = dom.$taxDetailsParent.querySelector<HTMLButtonElement>('#new-tax-exemption-cancel') as HTMLButtonElement;

    let taxUploads: number[] = [];

    countries.StateSelect(models.CountryID.COUNTRIES_UNITED_STATES, $stateSelect, {
        defaultUnitedStates: true,
        shortStates: true,
    }).then(() => {
        $stateSelect.addEventListener('change', () => {
            const shippingStateID = state.shippingOption?.address?.stateID;
            const needsWarning = $stateSelect.value !== 'all' && shippingStateID &&
                $stateSelect.value !== shippingStateID.toString();
            dom.$taxStateWarning.classList.toggle('d-none', !needsWarning);
        });
        if (ute?.stateID) {
            $stateSelect.value = ute.stateID.toString();
        } else {
            const shippingAddress = state.shippingOption?.address;
            if (shippingAddress?.stateID) {
                $stateSelect.value = shippingAddress.stateID.toString();
            }
        }
    }).catch(ShowError);

    if (ute) {
        dom.$taxToggleYes.click();
        $type.value = ute.userTaxExemptionReasonID?.toString() || '';
        $number.value = ute.number || '';
        $stateSelect.value = ute.stateID?.toString() || '';
        taxUploads = ute.userTaxExemptionUploads?.map(u => u.uploadID).filter((id): id is number => id !== undefined) || [];
        $taxConfirm.innerText = 'Save';
    }

    const taxUploader = new Uploader($taxUploaderLabel, {
        uploadIDs: taxUploads,
        confirmBtn: $taxConfirm,
    });

    if (taxUploader.getUploads().length) {
        $taxUploaderInput.setCustomValidity('Please upload a file');
    }

    $taxUploaderLabel.classList.value = 'uploader-dropzone validity-parent';
    dom.$taxDetailsParent.querySelector('.uploader-thumbnails')?.classList.add('mt-6');

    if (state.userTaxExemptions.length) {
        $taxCancel.classList.remove('d-none');
        $taxCancel.addEventListener('click', async () => {
            await initTax(state).then(() => dom.$taxToggleYes.click());
        });
    }

    $form.noValidate = true;
    $taxConfirm.addEventListener('click', async e => {
        e.preventDefault();
        $taxUploaderInput.setCustomValidity('');
        if (!taxUploader.getUploads().length) {
            $taxUploaderInput.setCustomValidity('Please upload a file');
        }
        if (!ReportValidity($form)) {
            return;
        }
        if (!$type.value || !$number.value || !state.customer) {
            return;
        }

        state.startLoading();
        $taxConfirm.disabled = true;

        try {
            const newUte = await NewUserTaxExemption({
                user: state.customer,
                userTaxExemptReasonID: parseInt($type.value, 10),
                number: $number.value,
                countryID: models.CountryID.COUNTRIES_UNITED_STATES,
                stateID: $stateSelect.value !== 'all' ? parseInt($stateSelect.value, 10) : undefined,
                uploadIDs: await taxUploader.getUploadIDs(),
            });
            // Edit mode: the customer-api has no full-update endpoint for
            // number/state/uploads, so the create above made a replacement.
            // Soft-delete the original now that the new one is committed —
            // skipping this leaves the saved list cluttered with the pre-edit
            // copy and breaks the user's mental model of "Save = update".
            // If the cleanup delete fails, surface the error and skip the
            // success toast — the new exemption is in place but the saved
            // list still carries the stale row, so don't claim the edit
            // landed cleanly.
            let cleanedUp = true;
            if (ute?.id) {
                try {
                    await DeleteUserTaxExemption(ute.id);
                } catch (err) {
                    cleanedUp = false;
                    ShowError(err);
                }
            }
            state.taxExemptionUploadThumbnails = taxUploader.getThumbnailPreviews({ readonly: true });
            state.taxExemption = newUte;
            state.taxStepComplete = true;
            if (cleanedUp) {
                const verb = ute ? 'updated' : 'added';
                toast.Success(`Your tax exemption has been ${verb}${state.sess?.user.id ? ' to your account' : ''}.`);
            }
            const shippingAddress = state.shippingOption?.address;
            const mismatch = newUte.stateID && shippingAddress?.stateID && newUte.stateID !== shippingAddress.stateID;
            dom.$taxStateWarning.classList.toggle('d-none', !mismatch);
            dom.$paymentBodyToggle?.click();
            await state.updateOrderSummary();
            await state.updateTaxExemptionSummary();
        } catch (err) {
            ShowError(err);
        } finally {
            $taxConfirm.disabled = false;
            state.stopLoading();
        }
    });
}

function bindTaxToggles(state: State): void {
    const { dom } = state;
    if (dom.$taxBody && dom.$taxDetailsParent) {
        [dom.$taxToggleYes, dom.$taxToggleNo].forEach($toggle => {
            $toggle.addEventListener('change', () => checkTaxSelected(state));
        });
    }
}

function bindTaxConfirmNone(state: State): void {
    const { dom } = state;
    dom.$taxConfirmNone.addEventListener('click', () => {
        state.taxExemption = undefined;
        state.taxExemptionUploadThumbnails = [];
        state.taxStepComplete = true;
        void state.updateOrderSummary();
        state.updateShippingSummary();
        void state.updateTaxExemptionSummary();
        updateTaxEditLink(state);
        dom.$paymentBodyToggle?.click();
    });
}

function bindTaxCollapse(state: State): void {
    const { dom } = state;
    dom.$taxBody?.addEventListener('shown.bs.collapse', () => {
        dom.$taxBodyToggle?.classList.add('d-none');
    });
    dom.$taxBody?.addEventListener('hidden.bs.collapse', () => updateTaxEditLink(state));

    dom.$taxBodyToggle?.addEventListener('click', () => {
        dom.$taxDetailsParent?.classList.toggle('d-none', !state.taxExemption);
        setTimeout(async () => {
            if (!dom.$taxBodyToggle?.classList.contains('collapsed')) {
                await initTax(state);
                setTimeout(() => {
                    dom.$taxBody?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 300);
            }
        }, 50);
    });
}

function updateTaxEditLink(state: State): void {
    const { dom } = state;
    const bodyOpen = dom.$taxBody?.classList.contains('show') || dom.$taxBody?.classList.contains('collapsing');
    dom.$taxBodyToggle?.classList.toggle('d-none', !state.taxStepComplete || Boolean(bodyOpen));
}

function bindTaxRadioClick(state: State): void {
    const { dom } = state;
    dom.$taxDetailsParent?.addEventListener('click', e => {
        const $target = e.target;
        if (!($target instanceof HTMLInputElement) || !$target.classList.contains('user-tax-exemption-input')) {
            return;
        }
        const ute = state.userTaxExemptions.find(u => u.id === $target.value);
        if (!ute) {
            return;
        }
        state.taxExemption = ute;
        state.taxExemptionUploadThumbnails = [];
        void state.updateOrderSummary();
        void state.updateTaxExemptionSummary();
        const shippingStateID = state.shippingOption?.address?.stateID;
        const mismatch = ute.stateID && shippingStateID && ute.stateID !== shippingStateID;
        dom.$taxStateWarning.classList.toggle('d-none', !mismatch);
    });
}
